'use client';

import {useMemo, useCallback} from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {useCommonData} from '@udemy-v2/common-data';
import {useClpPurchaseContext} from '../context/clp-purchase-context.react-component';
import {useClpCourse} from '../context/clp-course-provider.react-component';
import {publishEvent} from '@udemy-v2/event-tracking';
import {BuyNowEvent, EnrollNowEvent, LearningProduct} from '../events/events';
import {useOneClickCheckoutContext} from '@/lib/hooks/use-one-click-checkout-context';
import {OneClickCheckoutContext} from '@/types/one-click-checkout-context';

export const enum BuyButtonEventType {
    BUY_NOW = 'buy_now',
    ENROLL_NOW = 'enroll_now',
    GO_TO_COURSE = 'go_to_course',
}

export interface BuyButtonState {
    text?: string;
    checkoutUrl: string;
    trackEvent: () => void;
    isLoading: boolean;
    oneClickCheckoutContext?: OneClickCheckoutContext;
}

/**
 * A hook to determine the state and behavior of a transactional button (e.g., Buy Now, Enroll Now, Go to Course)
 * based on the user's enrollment status and course purchase options.
 */
export const useTransactionalButtonContext = () => {
    const {gettext} = useI18n();
    const commonData = useCommonData();
    const hasOrganization = !!commonData?.organization;
    const purchaseContext = useClpPurchaseContext();
    const course = useClpCourse();
    const {data: oneClickCheckoutContext, isLoading: isOneClickCheckoutLoading} =
        useOneClickCheckoutContext(Number(course.id));

    const courseId = course?.id;
    const buildCheckoutUrl = useCallback(
        (
            baseUrl: string,
            options: {
                source?: string;
                tracking?: Record<string, string>;
                referrer?: string;
            } = {},
        ) => {
            const url = new URL(baseUrl, window.location.origin);

            // Check if current page has display_type=mobile_app (equivalent to monolith's getIsMobileApp)
            const currentParams = new URLSearchParams(window.location.search);
            const isMobileApp = currentParams.get('display_type') === 'mobile_app';
            const couponCode = currentParams.get('couponCode');

            // TODO: I think only couponCode is needed here based on this monolith code: https://github.com/udemy/website-django/blob/master/udemy/payment/mixins.py#L220-L241
            // But I'll leave it for now to minimize the potential risks in this changeset.
            const params = {
                ref: options.source || 'course_landing_page',
                checkout_type: 'express',
                return_path: window.location.pathname,
                ...(isMobileApp && {display_type: 'mobile_app'}),
                ...(courseId && {course_id: courseId}),
                ...(options.referrer && {referrer: options.referrer}),
                ...(couponCode && {couponCode: couponCode}),
                ...(options.tracking || {}),
            };

            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });

            return url.toString();
        },
        [courseId],
    );

    const eventPayload = useMemo(() => {
        const courseId = Number(course.id);
        const courseTrackingId = course.frontendTrackingId || course.id;
        return {
            buyable: {
                id: courseId,
                type: LearningProduct.COURSE,
                trackingId: courseTrackingId,
            },
        };
    }, [course]);

    return useMemo((): BuyButtonState => {
        // Compound loading state - loading if either purchase context or one-click checkout is loading
        const isLoading = purchaseContext.isLoading || isOneClickCheckoutLoading;

        // If either is loading, return loading state with disabled button
        if (isLoading) {
            return {
                checkoutUrl: '',
                trackEvent: () => {},
                isLoading: true,
            };
        }

        if (!purchaseContext.isPublished) {
            return {
                text: gettext('Unavailable'),
                checkoutUrl: '',
                trackEvent: () => {},
                isLoading: false,
            };
        }
        if (purchaseContext.isEnrolledInCourse) {
            return {
                text: gettext('Go to course'),
                checkoutUrl: course.urlCourseTaking ?? '',
                trackEvent: () => {}, // No tracking needed for "Go to course"
                isLoading: false,
            };
        }
        if (
            purchaseContext.isPersonalPlanSubscriber &&
            purchaseContext.isCourseIncludedInUserSubscription
        ) {
            return {
                text: gettext('Enroll now'),
                checkoutUrl: course.urlCourseTaking ?? '',
                trackEvent: () => {}, // Currently no tracking implemented in monolith for CLP -> course taking page
                isLoading: false,
            };
        }
        if ((hasOrganization && purchaseContext.isPublished) || purchaseContext.isFree) {
            return {
                text: gettext('Enroll now'),
                checkoutUrl: course.urlAutoEnroll ?? '',
                trackEvent: () => publishEvent(new EnrollNowEvent(eventPayload)),
                isLoading: false,
            };
        }

        const baseCheckoutUrl = `/payment/checkout/express/course/${course.id}/`;

        if (purchaseContext.isFreeWithDiscount) {
            return {
                text: gettext('Enroll now'),
                checkoutUrl: buildCheckoutUrl(baseCheckoutUrl),
                trackEvent: () => publishEvent(new EnrollNowEvent(eventPayload)),
                isLoading: false,
            };
        }
        return {
            text: gettext('Buy now'),
            checkoutUrl: buildCheckoutUrl(baseCheckoutUrl),
            trackEvent: () => publishEvent(new BuyNowEvent(eventPayload)),
            isLoading: false,
            oneClickCheckoutContext,
        };
    }, [
        course.id,
        course.urlCourseTaking,
        course.urlAutoEnroll,
        purchaseContext.isLoading,
        purchaseContext.isEnrolledInCourse,
        purchaseContext.isPublished,
        purchaseContext.isFree,
        purchaseContext.isFreeWithDiscount,
        purchaseContext.isCourseIncludedInUserSubscription,
        purchaseContext.isPersonalPlanSubscriber,
        hasOrganization,
        buildCheckoutUrl,
        gettext,
        eventPayload,
        isOneClickCheckoutLoading,
        oneClickCheckoutContext,
    ]);
};
